import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { PhrasesSelectorContainer } from '../../../containers/PhrasesSelectorContainer/PhrasesSelectorContainer';
import { PhrasesSelectorFooterContainer } from '../../../containers/PhrasesSelectorFooterContainer/PhrasesSelectorFooterContainer';
import { Bem } from '../../../service/Bem/Bem';
import { UrlHelpers } from '../../../service/UrlHelpers';
import { LinkTarget, Tld } from '../../../typings/common';
import { GroupId } from '../../../typings/groups';
import { PhrasesSelectorWizardId } from '../../../typings/phrases';
import { HintItem, HintList } from '../../HintList/HintList';
import { ModalDecorator } from '../../ModalDecorator/ModalDecorator';
import { StyledLink, StyledLinkTheme } from '../../StyledLink/StyledLink';

import './PhrasesSelectorModal.css';
import * as keyset from './PhrasesSelectorModal.i18n';

export interface PhrasesSelectorModalProps {
    isOpen: boolean;
    isLoading: boolean;
    wizardId: PhrasesSelectorWizardId;
    groupId: GroupId;
    tld: Tld;
    hintItems: HintItem[];
    areMinusPhrasesEnabled: boolean;
    onClose: () => void;
    onSubmit: () => void;
}

const BLOCK = 'phrases-selector-modal';
const i18n = i18nFactory(keyset);

const PhrasesSelectorModalHeader = React.memo(
    (props: { tld: Tld; hintItems: HintItem[]; areMinusPhrasesEnabled: boolean }) => (
        <header className={Bem.cls(BLOCK, 'header')}>
            <h2 className={Bem.cls(BLOCK, 'header-title')}>{i18n('header-title')}</h2>
            <p className={Bem.cls(BLOCK, 'header-caption')}>
                <span>{props.areMinusPhrasesEnabled ? i18n('header-caption-cpc') : i18n('header-caption-cpm')}</span>
                <StyledLink
                    title={i18n('help-link')}
                    theme={StyledLinkTheme.blue}
                    href={UrlHelpers.getCommonHelpUrl('direct/new-interface/add-keywords.html', props.tld)}
                    target={LinkTarget.blank}
                >
                    {` — ${i18n('help-link')}`}
                </StyledLink>
            </p>
            <div className={Bem.cls(BLOCK, 'header-content')}>
                <HintList items={props.hintItems} />
            </div>
        </header>
    )
);

export const PhrasesSelectorModal = React.memo(
    ({
        wizardId,
        isLoading,
        isOpen,
        onClose,
        groupId,
        onSubmit,
        tld,
        hintItems,
        areMinusPhrasesEnabled
    }: PhrasesSelectorModalProps) => {
        const header = React.useMemo(
            () => (
                <PhrasesSelectorModalHeader
                    tld={tld}
                    hintItems={hintItems}
                    areMinusPhrasesEnabled={areMinusPhrasesEnabled}
                />
            ),
            [tld, hintItems, areMinusPhrasesEnabled]
        );
        const footer = React.useMemo(
            () => <PhrasesSelectorFooterContainer groupId={groupId} wizardId={wizardId} onSubmit={onSubmit} />,
            [groupId, wizardId, onSubmit]
        );

        return (
            <ModalDecorator
                visible={isOpen}
                hasClose={true}
                progress={isLoading}
                cls={BLOCK}
                bodyCls={Bem.cls(BLOCK, 'modal-body')}
                onClose={onClose}
                footer={footer}
                header={header}
            >
                <PhrasesSelectorContainer
                    cls={Bem.cls(BLOCK, 'phrase-selector-container')}
                    wizardId={wizardId}
                    groupId={groupId}
                />
            </ModalDecorator>
        );
    }
);
